import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Label } from '@clarix/ui';
import { useVerifyOtp, useResendOtp, normalizeError } from '@/core/api';
import { OtpInput } from './OtpInput';

/**
 * Step 2 of self-serve signup. On success, carries { email,
 * email_verification_token } forward via router state to
 * SignupWorkspacePage — NOT the URL and NOT persisted storage, since the
 * token is short-lived and this hop is the only place it needs to travel.
 * A direct visit here with no email (e.g. a refresh) has nothing to verify,
 * so it bounces back to /signup, mirroring the previous behaviour.
 */
export function VerifyOtpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  const [code, setCode] = useState('');
  const verify = useVerifyOtp();
  const resend = useResendOtp();
  const [resentMessage, setResentMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!email) navigate('/signup', { replace: true });
  }, [email, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = await verify.mutateAsync({ email, code });
      navigate('/signup/workspace', {
        replace: true,
        state: { email: data.email, emailVerificationToken: data.email_verification_token },
      });
    } catch {
      /* error surfaced below */
    }
  };

  const onResend = async () => {
    setResentMessage(null);
    try {
      await resend.mutateAsync({ email, purpose: 'SIGNUP' });
      setResentMessage('A new code is on its way.');
    } catch {
      /* error surfaced below */
    }
  };

  const error = verify.error ? normalizeError(verify.error) : null;
  const resendError = resend.error ? normalizeError(resend.error) : null;

  return (
    <div>
      <h1 className="mb-1 text-5xl font-semibold text-slate-900 dark:text-slate-100">
        Verify your email
      </h1>
      <p className="mb-6 text-xl text-slate-500">
        We sent a 6-digit code to <strong>{email}</strong>. Enter it below.
      </p>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <Label htmlFor="code">Verification code</Label>
          <div className="mt-2">
            <OtpInput
              value={code}
              onChange={setCode}
              disabled={verify.isPending}
              ariaLabel="Verification code"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {error.message}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={verify.isPending || code.length !== 6}>
          {verify.isPending ? 'Verifying…' : 'Verify and continue'}
        </Button>

        <div className="flex items-center justify-between text-xl">
          <button
            type="button"
            className="text-indigo-600 hover:underline disabled:opacity-50"
            onClick={onResend}
            disabled={resend.isPending}
          >
            Resend code
          </button>
          {resentMessage && <span className="text-green-600">{resentMessage}</span>}
          {resendError && <span className="text-red-600">{resendError.message}</span>}
        </div>
      </form>
    </div>
  );
}
